import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Analysis,
  Template,
  User,
  Campaign,
  AggregateComments,
  ItemType,
  OverallValues,
  CampaignState,
} from "../../types";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLabel,
  VictoryPortal,
  VictoryScatter,
  VictoryTheme,
  VictoryTooltip,
} from "victory";

import { useResizeDetector } from "react-resize-detector";
import { getUserName } from "../../DataTransform";
import FeedbackLoopColors from "./Colors";
import {
  IndividualTextColumns,
  getIndividualSelfPeerColumns,
  hasMultipleRaterTypes,
} from "./Columns";
import {
  friendlyLabel,
  sortAnalysesByLabel,
  tooltipFlyoutWidth,
  wrapLabelForTooltip,
} from "./labelHelpers";
import { HoverFlyout } from "./HoverFlyout";
import "./CellStyle.css";
import { StudentLoopResponse } from "../Results/StudentLoopResponse";
import { LoadingButton } from "@mui/lab";
import { useQuery } from "@apollo/client";
import { GET_LOOP_RESPONSES, LoopResponsesResult } from "../../Queries";
import { useUser } from "../../Contexts/UserContext";
import OverallBlock from "./blocks/OverallBlock";
import InsightSummaryBlock from "./blocks/InsightSummaryBlock";
import { OpportunitiesBlock } from "./blocks/OpportunitiesBlock";
import { GridHeaderFactory } from "./GridHeaderFactory";

function analysesToObjectArray(
  analyses: Analysis[],
  aggregates: AggregateComments[],
  sections: string[],
) {
  return sections.map((section, id) => ({
    id,
    section,
    analyses: analyses
      .filter((analysis) => analysis.question.category === section)
      .map((analysis, id) => ({
        id,
        section: analysis.question.category,
        question: analysis.question.label,
        user: getUserName(analysis.user),
        self: analysis.self,
        peers: analysis.others,
        gap: analysis.gap,
        othersInstructor: analysis.othersInstructor,
        othersStudent: analysis.othersStudent,
        othersLead: analysis.othersLead,
      })),
    aggregates: aggregates
      .filter((aggregate) => aggregate.question.category === section)
      .map((aggregate, id) => ({
        id,
        section: aggregate.question.category,
        question: aggregate.question.label,

        comments: aggregate.comments.map((comment) => ({
          ...comment,
          recipient: getUserName(comment.recipient),
          respondent: getUserName(comment.respondent),
        })),
      })),
  }));
}

export interface Props {
  title: string;
  campaign: Campaign;
  user: User;
  analyses: Analysis[];
  aggregates: AggregateComments[];
  overall: OverallValues;
  template: Template;
  resultsToken?: string;
  preview?: boolean;
  hasMixedAuthors?: boolean;
  hasMultipleRaterTypes?: boolean;
  authorType?: string;
  onAuthorTypeChange?: (v: string) => void;
  hideOverallAverages?: boolean;
}

export const AnonAnalysisView = ({
  campaign,
  user,
  analyses,
  aggregates,
  overall,
  template,
  resultsToken,
  preview = false,
  hasMixedAuthors = false,
  hasMultipleRaterTypes: hasMultipleRaterTypesProp,
  authorType = "",
  onAuthorTypeChange,
  hideOverallAverages = false,
}: Props) => {
  const studentLoopResponseId = "respond";
  // Prefer the authoritative backend-provided flag; fall back to computing
  // from template items (in case the prop is omitted, though items may be
  // filtered by visibility and miss cross-role recipient types).
  const multipleRaterTypes =
    hasMultipleRaterTypesProp ?? hasMultipleRaterTypes(template.items);
  const questions = template.items;
  // FL-238: question bars sorted alphabetically (natural) by label, reading
  // A→Z top-to-bottom. Victory's horizontal bars place index 1 at the BOTTOM,
  // so the alphabetically-ascending array is reversed for display: the
  // last-reversed element (alphabetically first) lands at the highest index =
  // top of the chart. Sorted/reversed once so the bar, scatter, tick values,
  // and tick-label components all index the same ordered array.
  const chartAnalyses = sortAnalysesByLabel(analyses).reverse();
  const sections = questions
    .map((question) => question.category)
    .filter((category, index, array) => array.indexOf(category) === index);

  const groupCount = questions.filter(
    (question) => question.itemType === ItemType.Choice,
  ).length;

  const id = "analysis-view";
  const { width: chartWidth, ref: chartRef } =
    useResizeDetector<HTMLDivElement>();
  const safeChartWidth = chartWidth ? chartWidth : 800;

  const groupOffset = 20;
  // FL-215: tightened per-question pitch — see View.tsx for rationale.
  const chartHeight = groupCount * 56 + 100;
  const safeChartHeight = chartHeight && chartHeight >= 200 ? chartHeight : 200;

  const { isStudent } = useUser();

  //
  // Queries
  //

  const { data: loopResponsesData } = useQuery<LoopResponsesResult>(
    GET_LOOP_RESPONSES,
    {
      variables: { campaignId: campaign.id, subjectId: user.id },
      context: {
        headers: resultsToken
          ? {
              Authorization: `JWT ${resultsToken}`,
            }
          : undefined,
      },
    },
  );

  //
  // Scroll to respond anchor when arriving via #respond hash
  //
  useEffect(() => {
    if (window.location.hash === `#${studentLoopResponseId}`) {
      const frame = requestAnimationFrame(() => {
        document
          .getElementById(studentLoopResponseId)
          ?.scrollIntoView({ behavior: "smooth" });
      });
      return () => cancelAnimationFrame(frame);
    }
  }, []);

  //
  // Helpers
  //
  const analysisObjectArray = analysesToObjectArray(
    analyses,
    aggregates,
    sections,
  );

  function prependWarning(text: string, outlier: boolean | undefined) {
    return outlier ? `⚠️ ${text}` : text;
  }

  // FL-215: scale the per-line wrap budget to the actual chart width so
  // labels never overflow on narrow viewports. Subtract the chart's left
  // padding + tick-label dx + a small safety margin, then divide by the
  // approximate glyph advance for the 14px label font (~7.5px). Floor at
  // 18 chars so the wrap stays usable even in extreme cases.
  const labelCharsPerLine = Math.max(
    18,
    Math.floor((safeChartWidth - 40) / 7.5),
  );

  // FL-215: render the question label directly above its bar instead of
  // beside it. The wrapped full text is still shown on hover (FL-209).
  const TooltipAxisLabel = (props: any) => {
    const [hovered, setHovered] = useState(false);
    const { datum, x = 0, y = 0 } = props;
    const idx = (typeof datum === "number" ? datum : 0) - 1;
    const a = chartAnalyses[idx];
    if (!a) return <VictoryLabel {...props} text={""} />;
    const fullText = prependWarning(a.label, a.outlier);
    const lines = friendlyLabel(a.label, labelCharsPerLine);
    const displayText = lines.map((line, i) =>
      i === 0 ? prependWarning(line, a.outlier) : line,
    );
    const wrappedFull = wrapLabelForTooltip(fullText, undefined, 40);
    return (
      <g
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <VictoryLabel {...props} text={displayText} />
        {hovered && (
          <VictoryPortal>
            <HoverFlyout x={x} y={y} lines={wrappedFull} />
          </VictoryPortal>
        )}
      </g>
    );
  };

  function handleRespond() {
    document
      .getElementById(studentLoopResponseId)
      ?.scrollIntoView({ behavior: "smooth" });
  }

  async function handleExportToPdf() {
    try {
      const resp = await fetch(
        `/api/campaigns/${campaign.id}/users/${user.id}/pdf`,
        {
          credentials: "include",
          headers: {
            Authorization: resultsToken ? `JWT ${resultsToken}` : "",
          },
        },
      );
      if (!resp.ok) {
        throw new Error(await resp.text());
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `campaign-${campaign.id}-user-${user.id}-pdf.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  }

  // Get student response from loop responses
  const studentResponse = loopResponsesData?.loopResponses?.find(
    (r) => r.loopType === "SR",
  );
  const studentHasResponded =
    studentResponse !== undefined && studentResponse.message.trim() !== "";

  return (
    <Stack spacing={4}>
      <Box sx={{ pt: 5 }}></Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <LoadingButton variant="text" onClick={handleExportToPdf}>
          Export to PDF
        </LoadingButton>
        {campaign?.loopStudent &&
          campaign?.state === CampaignState.Closed &&
          isStudent && (
            <Button
              variant="contained"
              disabled={studentHasResponded}
              onClick={handleRespond}
            >
              Respond
            </Button>
          )}
      </Box>

      {!hideOverallAverages && (
        <Stack spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Average Ratings</Typography>
            {hasMixedAuthors && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={authorType}
                  onChange={(e) =>
                    onAuthorTypeChange?.(e.target.value as string)
                  }
                  displayEmpty
                >
                  <MenuItem value="">All Authors</MenuItem>
                  <MenuItem value="instructor">Instructor-Authored</MenuItem>
                  <MenuItem value="peer">Peer-Authored</MenuItem>
                </Select>
              </FormControl>
            )}
          </Stack>
          <OverallBlock overall={overall} />
        </Stack>
      )}

      <Stack spacing={1}>
        <Typography variant="h6" sx={{ mt: 0.5 }}>
          Your Insights
        </Typography>
        <Stack spacing={2}>
          <InsightSummaryBlock analysisObjects={analysisObjectArray} />
          <OpportunitiesBlock
            analyses={analyses}
            safeChartWidth={safeChartWidth}
            user={user}
          />
        </Stack>
      </Stack>

      {aggregates.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 0.5 }}>
            Your Written Feedback
          </Typography>
          <div>
            {analysisObjectArray.map((section) =>
              section.aggregates.map((aggregate) => (
                <DataGrid
                  autoHeight={true}
                  key={aggregate.id}
                  hideFooter={
                    !aggregate.comments || aggregate.comments.length < 100
                  }
                  rows={aggregate.comments}
                  components={{
                    Toolbar: GridHeaderFactory(
                      `${section.section} - ${aggregate.question}`,
                    ),
                  }}
                  sx={{ backgroundColor: "black", mb: 1 }}
                  columns={IndividualTextColumns}
                  pagination={undefined}
                  getRowHeight={() => "auto"}
                />
              )),
            )}
          </div>
        </>
      )}

      <Stack spacing={2}>
        <Typography variant="h6" sx={{ mt: 0.5 }}>
          Full Results
        </Typography>
        <div style={{ height: safeChartHeight }} ref={chartRef}>
          {analyses.length > 0 && (
            <VictoryChart
              horizontal
              theme={VictoryTheme.material}
              padding={{ top: 48, bottom: 50, left: 16, right: 16 }}
              height={safeChartHeight}
              width={safeChartWidth}
              domain={{ x: [0.5, groupCount + 0.5], y: [0.5, 5.5] }}
            >
              <VictoryAxis
                tickValues={chartAnalyses.map((_, i) => i + 1)}
                style={{
                  axis: { stroke: "white" },
                  axisLabel: { padding: 20 },
                  grid: { stroke: "white" },
                  ticks: { stroke: "white", size: 0 },
                  tickLabels: {
                    padding: 0,
                    fill: "white",
                    fontSize: 14,
                    // FL-215: see View.tsx for rationale — paint-order halo
                    // tracks the rendered text width exactly, unlike Victory's
                    // approximateTextSize-driven backgroundStyle <rect>.
                    stroke: "rgba(0, 0, 0, 0.82)",
                    strokeWidth: 10,
                    strokeLinejoin: "round",
                    paintOrder: "stroke",
                  },
                }}
                tickLabelComponent={
                  // FL-215: lay labels above their bars (angle 0, left-anchored)
                  // instead of beside them (rotated -45). dx indents from the
                  // axis line; dy lifts the text above the bar's center.
                  <TooltipAxisLabel
                    angle={0}
                    textAnchor={"start"}
                    dx={10}
                    dy={-30}
                  />
                }
              />

              <VictoryAxis
                dependentAxis
                label="Rating"
                style={{
                  axis: { stroke: "white" },
                  axisLabel: { padding: 30 },
                  grid: { stroke: "white" },
                  ticks: { stroke: "white", size: 5 },
                  tickLabels: { padding: 5, stroke: "white", fill: "white" },
                }}
              />

              <VictoryBar
                barWidth={20}
                colorScale={FeedbackLoopColors}
                key={user.id}
                labelComponent={
                  <VictoryTooltip
                    constrainToVisibleArea
                    orientation={"top"}
                    flyoutPadding={{ top: 8, bottom: 8, left: 12, right: 12 }}
                    // eslint-disable-next-line react/prop-types
                    flyoutWidth={({ datum }) =>
                      // eslint-disable-next-line react/prop-types
                      tooltipFlyoutWidth(datum?.label)
                    }
                  />
                }
                name="bar"
                data={chartAnalyses.map((question, index) => ({
                  x: index + 1,
                  y: question.others,
                  label: wrapLabelForTooltip(
                    question.label,
                    `(Received: ${question.others?.toFixed(1)})`,
                  ),
                }))}
                style={{
                  data: {
                    stroke: "white",
                    strokeDasharray: 0,
                    strokeWidth: 2,
                    fill: FeedbackLoopColors[0],
                  },
                }}
              />

              <VictoryScatter
                key={user.id + "others"}
                style={{
                  data: {
                    fill: "white",
                  },
                }}
                size={6}
                labelComponent={
                  <VictoryTooltip
                    constrainToVisibleArea
                    orientation={"top"}
                    flyoutPadding={{ top: 8, bottom: 8, left: 12, right: 12 }}
                    // eslint-disable-next-line react/prop-types
                    flyoutWidth={({ datum }) =>
                      // eslint-disable-next-line react/prop-types
                      tooltipFlyoutWidth(datum?.label)
                    }
                  />
                }
                name="scatter"
                data={chartAnalyses.map((question, index) => ({
                  x: index + 1,
                  y: question.self,
                  label: wrapLabelForTooltip(
                    question.label,
                    `(SELF: ${question.self?.toFixed(1)})`,
                  ),
                  others: question.others,
                }))}
              />
            </VictoryChart>
          )}
        </div>

        <div id={id}>
          {analysisObjectArray.map((section) => (
            <>
              <DataGrid
                autoHeight={true}
                key={section.id}
                initialState={{
                  sorting: {
                    sortModel: [{ field: "gap", sort: "desc" }],
                  },
                }}
                hideFooter={!section.analyses || section.analyses.length < 100}
                rows={section.analyses}
                components={{
                  Toolbar: GridHeaderFactory(section.section),
                }}
                sx={{ backgroundColor: "black", mb: 1 }}
                columns={getIndividualSelfPeerColumns(multipleRaterTypes)}
                pagination={undefined}
              />
            </>
          ))}
        </div>
      </Stack>

      {(campaign?.loopStudent || campaign?.loopInstructor) && user && (
        <Stack spacing={2}>
          <Link id={studentLoopResponseId}></Link>
          <StudentLoopResponse
            campaignId={campaign.id}
            userId={user.id}
            resultsToken={resultsToken}
            showStudent={campaign.loopStudent}
            showInstructor={campaign.loopInstructor}
            preview={preview}
          />
        </Stack>
      )}
    </Stack>
  );
};
